import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { CurrentService } from '../current.service';
import { CustomerService } from '../customer.service';
import { FeatureSettingsService } from '../feature-settings/feature-settings.service';
import { HbmConfiguratorRegistry } from './hbm-configurator-registry.service';

/** Ouvre dans le tunnel un projet HomeByMe (Cuisine, Rangement, Salle de bain). */
@Injectable({ providedIn: 'root' })
export class HbmProjectOpenerService {
  private readonly current = inject(CurrentService);
  private readonly customer = inject(CustomerService);
  private readonly router = inject(Router);
  private readonly registry = inject(HbmConfiguratorRegistry);
  private readonly featureSettings = inject(FeatureSettingsService);
  private readonly tunnelNavigationStore = inject(TunnelNavigationStore);

  /** Retourne le contexte HBM correspondant au configurateur du projet, ou `undefined` s'il n'est pas HBM. */
  getContext(distributionID: string): CONFIG_CONTEXT | undefined {
    return this.registry.byDistributionID(distributionID)?.context;
  }

  /**
   * Charge la configuration du projet HomeByMe et navigue vers son tunnel, à l'étape accessible la plus avancée
   * au-delà de l'étape de départ du configurateur.
   * `reload` force la recréation du tunnel lorsque l'utilisateur s'y trouve déjà ; elle est implicite lorsque
   * l'utilisateur est déjà sur l'URL du tunnel cible.
   */
  async open(context: CONFIG_CONTEXT, hbmProjectId: string, isLegacy = false, reload = false): Promise<void> {
    const configurator = this.registry.byContext(context);
    if (!configurator) {
      throw new Error(`Contexte HBM inconnu : ${context}`);
    }

    await configurator.load(hbmProjectId);
    await this.current.loadHBMProject(hbmProjectId, isLegacy);
    this.customer.context = context;

    // startIndex 0 (SDB, 1ʳᵉ étape = Conception) : on garde l'URL propre `/tunnel`, comme à la création.
    const commands = configurator.startIndex > 0 ? ['tunnel', configurator.startIndex] : ['tunnel'];
    if (reload || this.router.url === this.router.createUrlTree(commands).toString()) {
      await this.router.navigateByUrl('/', { skipLocationChange: true });
    }
    await this.featureSettings.getSettings();
    this.tunnelNavigationStore.requestFurthestStep();
    let navigated = false;
    try {
      navigated = await this.router.navigate(commands);
    } finally {
      if (!navigated) {
        this.tunnelNavigationStore.cancelFurthestStep();
      }
    }
  }
}
